import React from "react";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import { CodeBlock } from "../markdown/code-block";
import { MemoizedReactMarkdown } from "../markdown/memoized-react-markdown";

/**
 * Props for the AssistantMessage component
 */
interface AssistantMessageProps {
  /** The message content to render (markdown supported) */
  message: string;
}

/**
 * AssistantMessage component renders assistant/AI messages with full markdown support.
 * Handles code blocks (inline and block), tables, headings, lists, blockquotes, and math.
 * Uses MemoizedReactMarkdown for performance and custom styling for all markdown elements.
 * 
 * @component
 * @param {AssistantMessageProps} props - Component props
 * @param {string} props.message - Markdown message content to render
 * @returns {JSX.Element} Rendered markdown message with custom styling
 * 
 * @example
 * ```tsx
 * <AssistantMessage message="# Hello\n\nThis is **bold** text." />
 * ```
 */
const AssistantMessage: React.FC<AssistantMessageProps> = ({ message }) => {
  return (
    <MemoizedReactMarkdown
      className="prose dark:prose-invert max-w-none"
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={[rehypeKatex]}
      components={{
        code({ node, inline, className, children, ...props }: any) {
          const isInline =
            inline ||
            (!className &&
              String(children).length < 100 &&
              !String(children).includes("\n"));
          if (isInline) {
            return (
              <code
                className="bg-[#e9ecef] dark:bg-gray-800 px-1.5 py-0.5 rounded text-sm font-mono"
                {...props}
              >
                {children}
              </code>
            );
          }
          const match = /language-(\w+)/.exec(className || "");
          return (
            <CodeBlock
              key={Math.random()}
              language={(match && match[1]) || ""}
              value={String(children).replace(/\n$/, "")}
              {...props}
            />
          );
        },
        table({ children }) {
          return (
            <table className="border-collapse border border-black px-3 py-1 dark:border-white">
              {children}
            </table>
          );
        },
        th({ children }) {
          return (
            <th className="break-words border border-black bg-gray-500 px-3 py-1 text-white dark:border-white">
              {children}
            </th>
          );
        },
        td({ children }) {
          return (
            <td className="break-words border border-black px-3 py-1 dark:border-white">
              {children}
            </td>
          );
        },
        h1({ children }) {
          return (
            <h1 className="text-2xl font-bold mt-6 first:mt-2 mb-4">
              {children}
            </h1>
          );
        },
        h2({ children }) {
          return (
            <h2 className="text-xl font-bold mt-5 first:mt-2 mb-3">
              {children}
            </h2>
          );
        },
        h3({ children }) {
          return (
            <h3 className="text-lg font-bold mt-4 first:mt-2 mb-2">
              {children}
            </h3>
          );
        },
        h4({ children }) {
          return (
            <h4 className="text-base font-bold mt-3 first:mt-2 mb-2">
              {children}
            </h4>
          );
        },
        h5({ children }) {
          return <h5 className="text-sm font-bold mt-2 mb-1">{children}</h5>;
        },
        h6({ children }) {
          return <h6 className="text-xs font-bold mt-2 mb-1">{children}</h6>;
        },
        ol({ children }) {
          return <ol className="list-decimal my-4 ml-6">{children}</ol>;
        },
        ul({ children }) {
          return <ul className="list-disc my-4 ml-6">{children}</ul>;
        },
        li({ children }) {
          return <li className="my-1">{children}</li>;
        },
        p({ children }) {
          return <p className="my-3">{children}</p>;
        },
        blockquote({ children }) {
          return (
            <blockquote className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic my-4 text-gray-700 dark:text-gray-300">
              {children}
            </blockquote>
          );
        },
      }}
    >
      {message}
    </MemoizedReactMarkdown>
  );
};

export default AssistantMessage;
